import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocalStorage } from "react-use";
import { useQueryState } from "next-usequerystate";
import orderBy from "lodash/orderBy";
import Analytics from "lib/analytics/analytics";
import { useAppRouter } from "lib/useAppRouter";
import { resetTraceCompanyId } from "lib/instrumentation/otelInstrumentation";
import { getClosestStaticRoute, Route, isDynamicRoute } from "lib/routes";
import { ACTIVE_COMPANY, CURRENT_ACTIVE_COMPANY } from "constants/localStorageKeys";
import { activeCompanyIdVar } from "apollo/apolloClient";
import type { InactiveCompanyFragment } from "../graphql.generated";
import { isPosthogFeatureFlagEnabled, FeatureFlag } from "lib/analytics/featureFlags";

/**
 * Cleans a query object by removing specified parameters, including those derived from dynamic path segments.
 *
 * @param currentQuery - The current router.query object.
 * @param currentPathname - The current router.pathname (template path, e.g., /transactions/[[...slug]]).
 * @param paramsToExplicitlyRemove - Parameter names to always remove if they exist (e.g., ['transactionId']).
 * @returns A new query object, cleaned of the specified parameters.
 */
const cleanQuery = (
  currentQuery: Record<string, any>,
  currentPathname: string,
  paramsToExplicitlyRemove: string[]
): Record<string, any> => {
  const cleanedQuery = { ...currentQuery };
  const dynamicKeysFromPath: string[] = [];
  const segments = currentPathname.split("/");
  for (const segment of segments) {
    if (segment.startsWith("[") && segment.endsWith("]")) {
      let paramName = segment.substring(1, segment.length - 1);
      // Handle catch-all routes like [...slug]
      if (paramName.startsWith("...")) {
        paramName = paramName.substring(3);
      }
      dynamicKeysFromPath.push(paramName);
    }
  }

  const allParamsToRemove = new Set([...dynamicKeysFromPath, ...paramsToExplicitlyRemove]);

  allParamsToRemove.forEach((param) => {
    delete cleanedQuery[param];
  });

  return cleanedQuery;
};

/**
 * Manages the active company ID, synchronizing URL query params and local storage.
 * Handles initialization, validation against available companies, fallback logic, and provides loading status.
 * Ensures the active company ID is always reflected in the URL query parameter `companyId` (unless it's a single company setup).
 * Initialization Priority: URL query parameter -> Local Storage -> Fallback (most recent company).
 *
 * @param companies - Array of companies the user has access to. An empty array is valid.
 * @param companiesLoading - Boolean indicating if the `companies` list is currently loading.
 * @returns An object containing:
 *   - activeCompanyId: The current active company ID (string, potentially empty).
 *   - setActiveCompanyId: Function to initiate the process of setting the active company ID.
 *   - isValidActiveCompany: Boolean indicating if `activeCompanyId` is valid *based on the currently loaded `companies` list*.
 *   - alignActiveCompanyWithObjectCompany: Function to set the active company based on an object's ID, redirecting if inaccessible.
 *   - isLoading: Boolean indicating if the hook is still determining the initial active company ID.
 */
export const useActiveCompanyId = (
  companies: InactiveCompanyFragment[],
  companiesLoading: boolean
) => {
  // --- 🏠 Hooks / State 🏠 ---

  const [queryCompanyId, setQueryCompanyId] = useQueryState("companyId");
  // Legacy localStorage hook - read-only during migration
  const [legacyStoredCompanyId] = useLocalStorage<string>(ACTIVE_COMPANY, "");
  // New localStorage hook - primary storage going forward
  const [newStoredCompanyId, setNewStoredCompanyId] = useLocalStorage<string>(
    CURRENT_ACTIVE_COMPANY,
    ""
  );
  const { router, isFirmRoute } = useAppRouter();
  const [isLoading, setIsLoading] = useState(true); // True until initial validation/fallback completes.

  // Internal active company ID state. Effect A determines the initial value.
  const [_activeCompanyId, _setActiveCompanyId] = useState<string | undefined>(undefined);

  // Ensures Effect A (initialization) runs only once.
  const hasRunInitialCheck = useRef(false);
  // Ref to hold the latest active company ID for use in callbacks, avoiding stale closures.
  const _activeCompanyIdRef = useRef(_activeCompanyId);

  // Determine the effective stored company ID with migration logic
  const effectiveStoredCompanyId = useMemo(() => {
    // Priority: CURRENT_ACTIVE_COMPANY -> ACTIVE_COMPANY (fallback)
    const effectiveId = newStoredCompanyId || legacyStoredCompanyId || "";

    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log("DEBUG_ID: effectiveStoredCompanyId calculated", {
        newStoredCompanyId,
        legacyStoredCompanyId,
        effectiveId,
        usingLegacyFallback: !newStoredCompanyId && !!legacyStoredCompanyId,
      });
    }

    return effectiveId;
  }, [newStoredCompanyId, legacyStoredCompanyId]);

  if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
    console.log("DEBUG_ID: useActiveCompanyId INITIAL RENDER/CALL", {
      companiesLoading,
      queryCompanyId,
      legacyStoredCompanyId,
      newStoredCompanyId,
      effectiveStoredCompanyId,
      _activeCompanyId,
    });
  }

  // --- 👷 Helper Functions 👷 ---

  /** Determines the target value for the 'companyId' URL query parameter. */
  const getTargetQueryParamValue = useCallback(
    (id: string | undefined | null) => {
      if (companies.length === 1) {
        return null;
      }
      return id ?? null;
    },
    [companies]
  );

  /** Checks if a given ID is valid against the available companies. */
  const isIdValid = useCallback(
    (
      id: string | null | undefined,
      availableCompanies: InactiveCompanyFragment[]
    ): id is string => {
      if (!id || availableCompanies.length === 0) {
        return false;
      }
      return availableCompanies.some((c) => c.id === id);
    },
    []
  );

  // --- ☎️ Callbacks ☎️ ---

  /**
   * Directly sets the active company ID in internal state, storage, and URL.
   * Handles navigation adjustments based on the new state.
   * @param id The company ID to set (or undefined to clear).
   */
  const setCompanyIdAndRoute = useCallback(
    (id: string | undefined) => {
      const previouslyActiveId = _activeCompanyIdRef.current;
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log("DEBUG_ID: setCompanyIdAndRoute CALLED", {
          id,
          previouslyActiveId,
          current_activeCompanyId: _activeCompanyId,
          queryCompanyId,
        });
      }

      // Internal state and storage updates (these should always happen if id changes or needs sync)
      if (id !== _activeCompanyId) {
        _setActiveCompanyId((currentInternalStateId) => {
          if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
            console.log("DEBUG_ID: _setActiveCompanyId UPDATER CALLED", {
              newId: id,
              currentInternalStateId,
            });
          }
          if (id === currentInternalStateId) return id; // Should be caught by outer check, but good safeguard
          if (id !== undefined || currentInternalStateId !== undefined) {
            if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
              console.log("DEBUG_ID: resetTraceCompanyId called from _setActiveCompanyId updater");
            }
            resetTraceCompanyId();
          }
          return id;
        });
      }
      if (id !== newStoredCompanyId) {
        // Only update localStorage when we have a valid company ID
        // This preserves the "activeCompany" in localStorage - we never clear or overwrite with empty values
        if (id !== undefined) {
          setNewStoredCompanyId(() => {
            if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
              console.log("DEBUG_ID: setNewStoredCompanyId UPDATER CALLED", {
                newStoredId: id,
                currentStoredId: newStoredCompanyId,
                migrationNote: "Writing to CURRENT_ACTIVE_COMPANY key",
                legacyStoredCompanyId: legacyStoredCompanyId,
              });
            }
            return id;
          });
        } else {
          if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
            console.log(
              "DEBUG_ID: setNewStoredCompanyId SKIPPED - id is undefined, preserving lastActiveCompany",
              {
                currentStoredId: newStoredCompanyId,
                migrationNote:
                  "Not clearing CURRENT_ACTIVE_COMPANY, preserving user's last active company",
              }
            );
          }
        }
      }

      // URL Query Parameter Update (should always try to sync if different)
      const targetQueryValue = getTargetQueryParamValue(id);
      if (queryCompanyId !== targetQueryValue) {
        if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
          console.log(
            "DEBUG_ID: setCompanyIdAndRoute - queryCompanyId !== targetQueryValue, calling setQueryCompanyId",
            { currentQuery: queryCompanyId, targetQueryValue }
          );
        }
        setQueryCompanyId(targetQueryValue);
      }

      // Navigation Logic
      if (id && isFirmRoute(router.asPath)) {
        // Firm route navigation
        if (id !== previouslyActiveId) {
          if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
            console.log(
              "DEBUG_ID: setCompanyIdAndRoute - navigating from firm route due to company change",
              { newCompanyId: id, currentPath: router.asPath }
            );
          }
          router.replace({ pathname: Route.home, query: { companyId: id } });
        }
      } else if (previouslyActiveId !== undefined && id !== previouslyActiveId) {
        const currentPathnameForDynamicCheck = router.pathname;
        const asPathWithoutQuery = router.asPath.split("?")[0];

        if (isDynamicRoute(currentPathnameForDynamicCheck)) {
          const targetStaticPath = getClosestStaticRoute(currentPathnameForDynamicCheck);
          if (asPathWithoutQuery !== targetStaticPath) {
            if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
              console.log(
                "DEBUG_ID: setCompanyIdAndRoute - navigating from dynamic route to its static base due to company change",
                {
                  newCompanyId: id,
                  previouslyActiveId,
                  currentPathname: currentPathnameForDynamicCheck,
                  currentAsPath: router.asPath,
                  targetStaticPath,
                }
              );
            }

            const cleanedQuery = cleanQuery(
              router.query,
              currentPathnameForDynamicCheck,
              ["slug"] // Explicitly remove slug, in addition to dynamic path params
            );
            cleanedQuery.companyId = id;
            router.replace({
              pathname: targetStaticPath,
              query: cleanedQuery,
            });
          } else if (router.query.slug && targetStaticPath === Route.transactions) {
            if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
              console.log(
                "DEBUG_ID: setCompanyIdAndRoute - on static target but slug query param exists. Cleaning slug.",
                { currentQuery: router.query, targetStaticPath }
              );
            }
            const cleanedQuery = cleanQuery(
              router.query,
              currentPathnameForDynamicCheck,
              ["slug"] // Explicitly remove slug
            );
            cleanedQuery.companyId = id;
            router.replace({
              pathname: targetStaticPath,
              query: cleanedQuery,
            });
          }
        } else if (router.query.slug && currentPathnameForDynamicCheck === Route.transactions) {
          if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
            console.log(
              "DEBUG_ID: setCompanyIdAndRoute - on static /transactions with slug query param. Cleaning slug.",
              { currentQuery: router.query }
            );
          }
          const cleanedQuery = cleanQuery(
            router.query,
            currentPathnameForDynamicCheck,
            ["slug"] // Explicitly remove slug
          );
          cleanedQuery.companyId = id;
          router.replace({
            pathname: currentPathnameForDynamicCheck,
            query: cleanedQuery,
          });
        }
      }

      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log("DEBUG_ID: setCompanyIdAndRoute FINISHED");
      }
    },
    [
      setNewStoredCompanyId,
      setQueryCompanyId,
      queryCompanyId,
      isFirmRoute,
      router,
      _setActiveCompanyId,
      getTargetQueryParamValue,
      newStoredCompanyId,
      _activeCompanyId,
    ]
  );

  /**
   * Initiates changing the active company.
   * Sets a pending ID, clears the URL query parameter, and relies on Effect B
   * to complete the change once the parameter is confirmed cleared.
   */
  const setActiveCompanyId = useCallback(
    (id: string | undefined) => {
      const currentId = _activeCompanyIdRef.current;
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log("DEBUG_ID: setActiveCompanyId CALLED", { id, currentId });
      }
      if (id === currentId) {
        if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
          console.log("DEBUG_ID: setActiveCompanyId - id === currentId, returning early");
        }
        return;
      }

      if (id !== undefined) {
        Analytics.companyChanged({ companyId: id });
      }
      setCompanyIdAndRoute(id);
    },
    [setCompanyIdAndRoute, _activeCompanyIdRef]
  );

  /**
   * Sets active company based on an object's company ID.
   * If user has access, initiates change. Otherwise, redirects to a non-dynamic route,
   * keeping the *current* active company ID in the URL.
   */
  const alignActiveCompanyWithObjectCompany = useCallback(
    (objectCompanyId?: string) => {
      // If objectCompanyId is not provided, there's nothing to align with.
      // The current active company remains active.
      if (objectCompanyId === undefined) {
        if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
          console.log(
            "DEBUG_ID: alignActiveCompanyWithObjectCompany - objectCompanyId is undefined, returning early."
          );
        }
        return;
      }

      const currentId = _activeCompanyIdRef.current;
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log("DEBUG_ID: alignActiveCompanyWithObjectCompany CALLED", {
          objectCompanyId,
          currentId,
          companiesLoading,
        });
      }
      if (currentId === objectCompanyId || companiesLoading) {
        if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
          console.log(
            "DEBUG_ID: alignActiveCompanyWithObjectCompany - currentId === objectCompanyId or companiesLoading, returning early"
          );
        }
        return;
      }

      const userHasAccessToObjectCompany = isIdValid(objectCompanyId, companies);
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log(
          "DEBUG_ID: alignActiveCompanyWithObjectCompany - userHasAccessToObjectCompany:",
          userHasAccessToObjectCompany
        );
      }

      if (userHasAccessToObjectCompany && objectCompanyId) {
        if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
          console.log(
            "DEBUG_ID: alignActiveCompanyWithObjectCompany - user has access, calling setActiveCompanyId",
            { objectCompanyId }
          );
        }
        setActiveCompanyId(objectCompanyId);
      } else {
        const targetPath = getClosestStaticRoute(router.pathname);
        // When redirecting because user doesn't have access, clean the query
        // based on the *original* dynamic path (router.pathname) before navigating to the static targetPath.
        // We explicitly want to remove parameters like 'slug' or other dynamic parts.
        const cleanedQueryForRedirect = cleanQuery(
          router.query,
          router.pathname, // Use the current, potentially dynamic pathname to identify params to remove
          ["slug"] // Explicitly ensure 'slug' is considered for removal, others derived from path.
        );

        const targetQuery = {
          ...cleanedQueryForRedirect,
          companyId: getTargetQueryParamValue(_activeCompanyIdRef.current),
        };

        if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
          console.log(
            "DEBUG_ID: alignActiveCompanyWithObjectCompany - user DOES NOT have access or no objectCompanyId, redirecting",
            { targetPath, targetQuery }
          );
        }
        router.replace({
          pathname: targetPath,
          query: targetQuery,
        });
      }
    },
    [
      setActiveCompanyId,
      companies,
      isIdValid,
      router,
      companiesLoading,
      getClosestStaticRoute,
      _activeCompanyIdRef,
      getTargetQueryParamValue,
    ]
  );

  // --- 🧊 Memoized Values 🧊 ---

  /** Is the current `_activeCompanyId` valid and companies loaded */
  const isValidActiveCompany = useMemo(() => {
    const isValid = !companiesLoading && isIdValid(_activeCompanyId, companies);
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log("DEBUG_ID: isValidActiveCompany RECALCULATED", {
        isValid,
        companiesLoading,
        _activeCompanyId,
        numCompanies: companies.length,
      });
    }
    return isValid;
  }, [companies, _activeCompanyId, isIdValid, companiesLoading]);

  // --- 💥 Effects 💥 ---

  // EFFECT A: Initial ID Setting, Validation & Fallback
  useEffect(() => {
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log("DEBUG_ID: EFFECT A - ENTER", {
        companiesLoading,
        hasRunInitialCheck: hasRunInitialCheck.current,
        queryCompanyId,
        legacyStoredCompanyId,
        newStoredCompanyId,
        _activeCompanyId,
      });
    }

    if (companiesLoading || hasRunInitialCheck.current) {
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log("DEBUG_ID: EFFECT A - returning early", {
          companiesLoading,
          hasRunInitialCheck: hasRunInitialCheck.current,
        });
      }
      return;
    }

    let idToUse: string | undefined = undefined;

    if (isIdValid(queryCompanyId, companies)) {
      idToUse = queryCompanyId;
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log("DEBUG_ID: EFFECT A - Using valid queryCompanyId:", idToUse);
      }
    } else if (isIdValid(effectiveStoredCompanyId, companies)) {
      idToUse = effectiveStoredCompanyId;
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        const usingLegacyFallback = !newStoredCompanyId && !!legacyStoredCompanyId;
        console.log(
          "DEBUG_ID: EFFECT A - Using valid effectiveStoredCompanyId (query was invalid or null):",
          idToUse,
          {
            migrationInfo: {
              usingLegacyFallback,
              newStoredCompanyId,
              legacyStoredCompanyId,
              willMigrateOnNextWrite: usingLegacyFallback,
            },
          }
        );
      }
    } else if (companies.length > 0) {
      const sortedCompanies = orderBy(companies, (c) => c.createdAt, ["desc"]);
      idToUse = sortedCompanies[0].id;
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log(
          "DEBUG_ID: EFFECT A - Fallback to most recent company (query and stored were invalid or null):",
          idToUse
        );
      }
    } else {
      // No valid query, no valid store, no companies to fallback to
      idToUse = undefined;
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log(
          "DEBUG_ID: EFFECT A - No valid ID found and no companies to fallback to, using undefined."
        );
      }
    }

    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log("DEBUG_ID: EFFECT A - Calling setCompanyIdAndRoute with final idToUse:", idToUse);
    }
    setCompanyIdAndRoute(idToUse);

    hasRunInitialCheck.current = true;
    setIsLoading(false);
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log("DEBUG_ID: EFFECT A - FINISHED, setIsLoading(false), hasRunInitialCheck=true");
    }
  }, [
    companies,
    companiesLoading,
    queryCompanyId,
    effectiveStoredCompanyId,
    setCompanyIdAndRoute,
    isIdValid,
    // effectiveStoredCompanyId checks both newStoredCompanyId and legacyStoredCompanyId
    // hasRunInitialCheck is a ref, not needed in deps
    // setIsLoading is stable
  ]);

  // EFFECT C: URL Query Parameter Synchronization / Conflict Resolution
  useEffect(() => {
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log("DEBUG_ID: EFFECT C - ENTER", {
        isLoading,
        hasRunInitialCheck: hasRunInitialCheck.current,
        queryCompanyId,
        _activeCompanyId,
      });
    }

    if (isLoading || !hasRunInitialCheck.current) {
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log("DEBUG_ID: EFFECT C - returning early (isLoading or !hasRunInitialCheck)");
      }
      return;
    }

    const expectedQueryParamForInternal = getTargetQueryParamValue(_activeCompanyId);

    const queryIdRepresentsChange =
      queryCompanyId !== _activeCompanyId && queryCompanyId !== expectedQueryParamForInternal;

    if (queryIdRepresentsChange) {
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log(
          "DEBUG_ID: EFFECT C - queryCompanyId ('" +
            queryCompanyId +
            "') potentially represents a change from internal ('" +
            _activeCompanyId +
            "')."
        );
      }
      if (isIdValid(queryCompanyId, companies)) {
        if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
          console.log(
            "DEBUG_ID: EFFECT C - Valid queryCompanyId ('" +
              queryCompanyId +
              "'). Syncing internal state."
          );
        }
        setCompanyIdAndRoute(queryCompanyId);
        return;
      } else {
        // queryCompanyId is present, represents a change, but is invalid.
        // Revert URL to match the valid internal state.
        // Only revert if queryCompanyId is not already what we expect for the current internal state (e.g. not already null for single company)
        if (queryCompanyId !== expectedQueryParamForInternal) {
          if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
            console.log(
              "DEBUG_ID: EFFECT C - Invalid queryCompanyId ('" +
                queryCompanyId +
                "') that represented change. Reverting URL to match internal state's expected ('" +
                expectedQueryParamForInternal +
                "')."
            );
          }
          setQueryCompanyId(expectedQueryParamForInternal);
          return;
        }
      }
    }

    // Fallback: Internal state (_activeCompanyId) is the source of truth, or no meaningful change from queryCompanyId.
    // Ensure URL matches the current internal state.
    // This handles initial load, fallback, programmatic changes via setActiveCompanyId, or when queryIdRepresentsChange is false.
    if (queryCompanyId !== expectedQueryParamForInternal) {
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log(
          "DEBUG_ID: EFFECT C - Internal state or no query change. Syncing URL from ('" +
            queryCompanyId +
            "') to expected ('" +
            expectedQueryParamForInternal +
            "') based on internal ID ('" +
            _activeCompanyId +
            "')."
        );
      }
      setQueryCompanyId(expectedQueryParamForInternal);
    } else {
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log(
          "DEBUG_ID: EFFECT C - queryCompanyId ('" +
            queryCompanyId +
            "') and expected ('" +
            expectedQueryParamForInternal +
            "') are in sync. No action."
        );
      }
    }

    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log("DEBUG_ID: EFFECT C - FINISHED");
    }
  }, [
    queryCompanyId,
    companies,
    companiesLoading,
    isLoading,
    _activeCompanyId,
    isIdValid,
    setQueryCompanyId,
    setCompanyIdAndRoute,
    getTargetQueryParamValue,
  ]);

  // EFFECT D: Update the ref with the latest active company ID
  useEffect(() => {
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
      console.log(
        "DEBUG_ID: EFFECT D - Updating _activeCompanyIdRef.current from",
        _activeCompanyIdRef.current,
        "to",
        _activeCompanyId
      );
    }
    _activeCompanyIdRef.current = _activeCompanyId;
  }, [_activeCompanyId]);

  // EFFECT E: Sync the active company ID with Apollo Client's reactive variable
  useEffect(() => {
    const companyId = _activeCompanyId ?? "";
    if (activeCompanyIdVar() !== companyId) {
      if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
        console.log(
          "DEBUG_ID: EFFECT E - Syncing activeCompanyIdVar from",
          activeCompanyIdVar(),
          "to",
          companyId
        );
      }
      activeCompanyIdVar(companyId);
    }
  }, [_activeCompanyId]);

  // --- ✅ Return Value ✅ ---
  const returnVal = {
    activeCompanyId: _activeCompanyId ?? "",
    setActiveCompanyId,
    isValidActiveCompany,
    alignActiveCompanyWithObjectCompany,
    isLoading,
    isIdValid,
  };
  if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging)) {
    const migrationStatus = {
      hasNewKey: !!newStoredCompanyId,
      hasLegacyKey: !!legacyStoredCompanyId,
      usingLegacyFallback: !newStoredCompanyId && !!legacyStoredCompanyId,
      migrationComplete: !!newStoredCompanyId,
    };
    console.log("DEBUG_ID: useActiveCompanyId RETURNING", {
      ...returnVal,
      migrationStatus,
    });
  }
  return returnVal;
};
